/** Budget model (per category and month, budgeted income) and balance anchors. */
import { shiftMonth } from "@/domain/accounting-period";
import type { BalanceAnchor, BudgetLine, IncomeBudget, YearMonth } from "@/domain/model";
import { Decimal, MoneyError, parseAmountInput } from "@/domain/money";
import { assertValid, balanceAnchorIssues, budgetLineIssues, incomeBudgetIssues } from "@/domain/validation";
import { newId } from "@/lib/hash";
import { nowIso, type BudgetDatabase } from "./db";
import { loadReferenceContext } from "./reference";

/** Budget inputs may be zero ("0" means "no spending planned"). Empty means "no budget line". */
function parseBudgetInput(raw: string): string {
  const text = raw.trim();
  if (/^0+([.,]0*)?$/.test(text)) return "0.00";
  return parseAmountInput(text);
}

/** Signed amount for balances ("-120,50" allowed). */
export function parseSignedAmountInput(raw: string): string {
  const text = raw.trim();
  const negative = text.startsWith("-");
  const body = negative ? text.slice(1) : text;
  if (/^0+([.,]0*)?$/.test(body)) return "0.00";
  const value = new Decimal(parseAmountInput(body));
  return (negative ? value.neg() : value).toFixed(2);
}

export async function setCategoryBudget(db: BudgetDatabase, accountingMonth: YearMonth, categoryId: string, amountInput: string): Promise<void> {
  const id = `${accountingMonth}|${categoryId}`;
  if (amountInput.trim() === "") {
    await db.budgets.delete(id);
    return;
  }
  const ctx = await loadReferenceContext(db);
  const now = nowIso();
  const previous = await db.budgets.get(id);
  const line: BudgetLine = {
    id,
    accountingMonth,
    categoryId,
    amount: parseBudgetInput(amountInput),
    source: "manual",
    createdAt: previous?.createdAt ?? now,
    updatedAt: now,
  };
  assertValid("Budget", budgetLineIssues(line, ctx));
  await db.budgets.put(line);
}

export async function setIncomeBudget(db: BudgetDatabase, accountingMonth: YearMonth, salaryInput: string, extraInput: string): Promise<void> {
  const now = nowIso();
  const previous = await db.incomeBudgets.get(accountingMonth);
  const value = (raw: string) => (raw.trim() === "" ? "0.00" : parseBudgetInput(raw));
  const budget: IncomeBudget = {
    id: accountingMonth,
    accountingMonth,
    salary: value(salaryInput),
    extra: value(extraInput),
    source: "manual",
    createdAt: previous?.createdAt ?? now,
    updatedAt: now,
  };
  assertValid("Entrate previste", incomeBudgetIssues(budget));
  await db.incomeBudgets.put(budget);
}

/** Copies category and income budgets from the previous month into an empty month. */
export async function copyBudgetFromPreviousMonth(db: BudgetDatabase, accountingMonth: YearMonth): Promise<number> {
  const from = shiftMonth(accountingMonth, -1);
  const source = await db.budgets.where("accountingMonth").equals(from).toArray();
  if (source.length === 0) throw new MoneyError("Il mese precedente non ha un budget da copiare.");
  if ((await db.budgets.where("accountingMonth").equals(accountingMonth).count()) > 0) {
    throw new MoneyError("Questo mese ha già un budget: modificalo riga per riga.");
  }
  const now = nowIso();
  const incomeSource = await db.incomeBudgets.get(from);
  await db.transaction("rw", [db.budgets, db.incomeBudgets], async () => {
    await db.budgets.bulkPut(
      source.map((b) => ({ ...b, id: `${accountingMonth}|${b.categoryId}`, accountingMonth, source: "manual" as const, createdAt: now, updatedAt: now })),
    );
    if (incomeSource && !(await db.incomeBudgets.get(accountingMonth))) {
      await db.incomeBudgets.put({ ...incomeSource, id: accountingMonth, accountingMonth, source: "manual", createdAt: now, updatedAt: now });
    }
  });
  return source.length;
}

export async function saveBalanceAnchor(
  db: BudgetDatabase,
  input: { id?: string; effectiveMonth: YearMonth; amount: string; label: string },
): Promise<BalanceAnchor> {
  const now = nowIso();
  const previous = input.id ? await db.balanceAnchors.get(input.id) : undefined;
  const anchor: BalanceAnchor = {
    id: previous?.id ?? newId(),
    effectiveMonth: input.effectiveMonth,
    amount: parseSignedAmountInput(input.amount),
    label: input.label.trim(),
    source: previous?.source ?? "manual",
    createdAt: previous?.createdAt ?? now,
    updatedAt: now,
  };
  assertValid("Saldo", balanceAnchorIssues(anchor));
  await db.balanceAnchors.put(anchor);
  return anchor;
}

export async function deleteBalanceAnchor(db: BudgetDatabase, id: string): Promise<void> {
  await db.balanceAnchors.delete(id);
}
